import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Props } from '../@types';
import { AuthContextType, User } from '../@types/auth';
import { decodePayload } from '../utils/decodePayload';
import { LoginPaper } from '../components/Login';
import { invalidateKeycloakSession, removeAllAuthCookies, validateSession } from '../utils/authCookies';

const KEYCLOAK_TOKEN_ENDPOINT = process.env.REACT_APP_OAUTH2_PROVIDER_REFRESH_ENDPOINT;
const AUTH_COOKIE_DOMAIN = process.env.REACT_APP_AUTH_COOKIE_DOMAIN;

const ACCESS_TOKEN_COOKIE_NAME = 'accessToken';
const REFRESH_TOKEN_COOKKIE_NAME = 'refreshToken';

const AuthContext = createContext<AuthContextType>({
    user: undefined,
    isAuthenticated: false,
    signIn: () => console.log('signIn'),
    signOut: () => console.log('signOut'),
    setAuthToken: (token: string) => console.log('setToken'),
    setRefreshToken: (token: string) => console.log('setToken'),
    refreshTheToken: () => Promise.resolve(undefined),
});

const getCookieByName = (name: string): string | undefined => {
    const cookies = document.cookie.split('; ');
    return cookies?.find(cookie => cookie.startsWith(`${name}=`))?.replace(`${name}=`, '');
};

const setCookieByName = (name: string, value: string) => {
    const { exp } = decodePayload(value);
    document.cookie = name + '=' + value + '; domain=' + AUTH_COOKIE_DOMAIN + '; path=/; expires=' + new Date(exp * 1000).toString();
};

type AuthState = AuthContextType & {
    isLoading: boolean;
    getValidToken: () => Promise<string | undefined>;
};

const useProvideAuth = (): AuthState => {
    const [user, setUser] = useState<User>();
    const [isLoading, setIsLoading] = useState(true);

    const getAuthToken = () => {
        return getCookieByName(ACCESS_TOKEN_COOKIE_NAME);
    };

    const getRefreshToken = () => {
        return getCookieByName(REFRESH_TOKEN_COOKKIE_NAME);
    };

    const signOut = () => {
        invalidateKeycloakSession();
        setUser(undefined);
        removeAllAuthCookies();
    };

    const setAuthToken = (token: string) => {
        setCookieByName(ACCESS_TOKEN_COOKIE_NAME, token);
        setUser(prev => (prev ? { ...prev, accessToken: token } : undefined));
    };

    const setRefreshToken = (token: string) => {
        setCookieByName(REFRESH_TOKEN_COOKKIE_NAME, token);
        setUser(prev => (prev ? { ...prev, refreshToken: token } : undefined));
    };

    const signIn = async () => {
        const accessToken = getAuthToken();
        const refreshToken = getRefreshToken();

        if (!accessToken || !refreshToken) {
            return;
        }

        try {
            const { exp, preferred_username, family_name, given_name, email, realm_access, workplace } = decodePayload(accessToken);

            const resolvedUser: User = {
                username: preferred_username,
                name: `${family_name} ${given_name}`,
                email,
                roles: realm_access.roles,
                accessToken,
                refreshToken,
                exp,
                workplace,
            };
            setUser(resolvedUser);
        } catch (e) {
            console.error(e);
            signOut();
        }
    };

    const isRefreshing = useRef(false);

    const refreshAccessToken = async (): Promise<string | undefined> => {
        const refreshToken = getRefreshToken();
        if (isRefreshing.current || !KEYCLOAK_TOKEN_ENDPOINT || !refreshToken) {
            return;
        }

        const { exp: refreshExp } = decodePayload(refreshToken);
        if (new Date().getTime() > refreshExp * 1000) {
            signOut();
            return;
        }

        isRefreshing.current = true;

        try {
            const response = await fetch(KEYCLOAK_TOKEN_ENDPOINT, {
                method: 'POST',
                headers: {
                    'Content-type': 'application/x-www-form-urlencoded',
                },
                body: new URLSearchParams({
                    client_id: 'buszrent-crm',
                    refresh_token: refreshToken,
                    grant_type: 'refresh_token',
                }),
            });

            if (response.status === 401) {
                signOut();
                return;
            }
            const body = await response.json();
            setCookieByName(ACCESS_TOKEN_COOKIE_NAME, body.access_token);
            setCookieByName(REFRESH_TOKEN_COOKKIE_NAME, body.refresh_token);
            await signIn();

            return body.access_token;
        } catch (e) {
            signOut();
            return;
        } finally {
            isRefreshing.current = false;
        }
    };

    const getValidToken = async (): Promise<string | undefined> => {
        const accessToken = getAuthToken();

        if (accessToken) {
            const { exp } = decodePayload(accessToken);
            const oneMinuteFromNow = new Date();
            oneMinuteFromNow.setMinutes(oneMinuteFromNow.getMinutes() + 1);

            if (oneMinuteFromNow.getTime() <= exp * 1000) {
                return accessToken;
            }
        }

        return refreshAccessToken();
    };

    const checkTokenConsistency = () => {
        const cookieToken = getAuthToken();
        if (!cookieToken && user) {
            signOut();
            return;
        }
        if (cookieToken && user && cookieToken !== user?.accessToken) {
            signOut();
        }
    };

    useEffect(() => {
        const init = async () => {
            const isValid = await validateSession();
            if (isValid) {
                await signIn();
            } else if (getRefreshToken()) {
                await refreshAccessToken();
            } else {
                removeAllAuthCookies();
            }
            setIsLoading(false);
        };
        init();
    }, []);

    useEffect(() => {
        const onVisibilityChange = async () => {
            if (document.visibilityState === 'visible') {
                checkTokenConsistency();
                await getValidToken();
            }
        };

        document.addEventListener('visibilitychange', onVisibilityChange);
        return () => document.removeEventListener('visibilitychange', onVisibilityChange);
    }, []);

    return {
        user,
        isAuthenticated: user !== undefined && !!user.accessToken,
        isLoading,
        signIn,
        signOut,
        setAuthToken,
        setRefreshToken,
        getValidToken,
        refreshTheToken: getValidToken,
    };
};

const useAuth = () => {
    return useContext(AuthContext);
};

const ProvideAuth = ({ children }: Props) => {
    const auth = useProvideAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const search = location.search;
    const queryParams = new URLSearchParams(search);

    useEffect(() => {
        const initAuth = async () => {
            const accessFromQuery = queryParams.get('t') ?? queryParams.get(ACCESS_TOKEN_COOKIE_NAME) ?? undefined;
            const refreshFromQuery = queryParams.get('r') ?? queryParams.get(REFRESH_TOKEN_COOKKIE_NAME) ?? undefined;

            const accessFromCookie = getCookieByName(ACCESS_TOKEN_COOKIE_NAME);
            const refreshFromCookie = getCookieByName(REFRESH_TOKEN_COOKKIE_NAME);

            const access = accessFromQuery ?? accessFromCookie;
            const refresh = refreshFromQuery ?? refreshFromCookie ?? '';

            const hadQueryToken = queryParams.has('t') || queryParams.has('r');

            if (hadQueryToken && access && refresh && auth.signIn && typeof auth.signIn === 'function') {
                auth.setAuthToken(access);
                auth.setRefreshToken(refresh);
                auth.signIn();

                queryParams.delete('t');
                queryParams.delete('r');
                const nextSearch = queryParams.toString();
                navigate(location.pathname + (nextSearch ? `?${nextSearch}` : ''), { replace: true });
            }
        };
        initAuth();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (auth.user) {
            auth.getValidToken();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    const { isLoading, getValidToken: _getValidToken, ...contextValue } = auth;

    if (isLoading) {
        return null;
    }

    if (!auth.user) {
        return <LoginPaper />;
    }

    return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

export { useAuth, useProvideAuth, ProvideAuth };
